import { useStripePaymentsEnabled } from "../../../hooks/useStripePaymentsEnabled";
import {
  email,
  FunctionField,
  Labeled,
  NumberField,
  NumberInput,
  SelectField,
  TextField,
  TextInput,
  useGetOne,
  useRecordContext,
} from "react-admin";
import {
  Avatar,
  Badge,
  Box,
  Card,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LocalPhoneIcon from "@mui/icons-material/LocalPhoneOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import EditIcon from "@mui/icons-material/Edit";
import { OpenInFirestoreMenuItem } from "../../../menu-items/OpenInFirestoreMenuItem";
import { OpenInHubspotMenuItem } from "../../../menu-items/OpenInHubspotMenuItem";
import { OpenInStripeMenuItem } from "../../../menu-items/OpenInStripeMenuItem";
import { Student } from "../../../providers/studentsProvider";
import { Row } from "../../../misc/Row";
import { NameHeader } from "../../../misc/NameHeader";
import { StudentLabels } from "../../../misc/StudentLabels";
import { autovioColors, dotStyle2, overviewCardStyle } from "../../../misc/backofficeTheme";
import { StudentLabelsMenu } from "../../../misc/StudentLabelsMenu";
import { useMenu } from "../../../hooks/useMenu";
import { grants } from "../../../backoffice.access_control";
import { AgeField } from "../../../fields/AgeField";
import { DateField } from "../../../fields/DateField";
import { AddressDisplay } from "../../../misc/AddressDisplay";
import { getGoogleMapsUrl } from "../../../utils/getGoogleMapsUrl";
import { MoneyField } from "../../../fields/MoneyField";
import { AuthProvidersField } from "../../../fields/AuthProvidersField";
import { LabeledAndEditable } from "../../../misc/LabeledAndEditable";
import { FundingInstructionsField } from "../../../fields/FundingInstructionsField";
import { InvoiceAddressField } from "../../../fields/address/InvoiceAddressField";
import { ResetPasswordLink } from "../../../links/ResetPasswordLink";
import { StudentFeatureToggles } from "../../../inputs/StudentFeatureToggles";
import { Column } from "../../../misc/Column";
import { DateInput } from "../../../inputs/DateInput";
import { CentsInput } from "../../../inputs/MoneyInput";
import { CreateDocumentMenu } from "../CreateDocumentMenu";
import { useDialog } from "../../../hooks/useDialog";
import { NationalityField } from "../../../fields/NationalityField";
import { NationalityInput } from "../../../inputs/NationalityInput";
import { OpenPreTheoryExamWorkflowDialogMenuItem } from "../../../menu-items/OpenPreTheoryExamWorkflowDialogMenuItem";
import { PreTheoryExamWorkflowDialog } from "../../../misc/PreTheoryExamWorkflowDialog";
import { TheoryExamLanguageField } from "../../../fields/TheoryExamLanguageField";
import { TheoryExamLanguageInput } from "../../../inputs/TheoryExamLanguageInput";
import { EmailIcon } from "../../../icons/EmailIcon";
import { WhatsappIcon } from "../../../icons/WhatsappIcon";
import { GENDER_CHOICES } from "../../../model/Gender";
import { SalutationInput } from "../../../inputs/SalutationInput";
import { RadioGroupInput } from "../../../inputs/RadioGroupInput";
import { BackButton } from "../../../buttons/BackButton";
import { LabeledBox } from "../../../misc/LabeledBox";
import { FlatAccordion } from "../../../misc/FlatAccordion";
import { EditPostalAddressDialog } from "../../../dialogs/EditPostalAddressDialog";
import { ChangeStudentStatusDialog } from "../../../dialogs/ChangeStudentStatusDialog";
import { EditStudentGuardianDialog } from "../../../dialogs/EditStudentGuardianDialog";
import {
  ChangeStudentPaymentStrategyDialog,
  PaymentStrategyChoices,
} from "../../../dialogs/ChangeStudentPaymentStrategyDialog";
import { EditInvoiceAddressDialog } from "../../../dialogs/EditInvoiceAddressDialog";
import { EditStudentNameDialog } from "../../../dialogs/EditStudentNameDialog";
import { CopyIdIntoClipboardMenuItem } from "../../../menu-items/CopyIdIntoClipboardMenuItem";
import { CopyVogelIdIntoClipboardMenuItem } from "../../../menu-items/CopyVogelIdIntoClipboardMenuItem";
import { AutoSubmitSelectInput } from "../../../inputs/AutoSubmitSelectInput";
import { EyeGlassesIcon } from "../../../icons/EyeGlassesIcon";
import { Duration } from "luxon";
import { EyeGlassesMenuItem } from "../../../menu-items/EyeGlassesMenuItem";
import { ArchivedStudentMenuItem } from "../../../menu-items/ArchivedStudentMenuItem";
import { HubspotIdMenuItem } from "../../../menu-items/HubspotIdMenuItem";
import { HubspotIdDialog } from "../../../dialogs/HubspotIdDialog";
import { formatDate } from "../../../utils/calendar";
import { EditDrivingLicenseClassesDialog } from "../../../dialogs/EditDrivingLicenseClassesDialog";
import { AUTOVIO_SUPPORTED_LANGUAGES } from "../../../inputs/TheoryExamLanguageInput";
import { nationalities } from "i18n-nationality/langs/de.json";
import { StudentTheoryBranchesDialog } from "../../../dialogs/StudentTheoryBranchesDialog";
import { DrivingSchool, drivingSchoolsProvider } from "../../../providers/drivingSchoolsProvider";
import { isForCarOnly } from "../../../model/DrivingLicenseClass";
import { DRIVING_LICENSE_CLASSES_CHOICES } from "../../../model/DrivingLicenseClass";
import { getAuthenticatedServerClient, schemas } from "../../../api/server.api";
import { useQuery } from "react-query";
import { useState } from "react";
import { z } from "zod";
import { ExternalTrainingProgressDialog } from "../../../dialogs/ExternalTrainingProgressDialog";
import { ExternalTrainingProgressField } from "../ExternalTrainingProgressField";

type ExternalTrainingProgress = z.infer<typeof schemas.ExternalTrainingProgressDto>;

const inputFieldStyle = {
  "& .MuiOutlinedInput-input ": {
    padding: "9px 10px",
  },
};

const BF17_CHOICES: Array<[string, string]> = [
  ["true", "Ja"],
  ["false", "Nein"],
];

const profileCardStyles = {
  authProviderField: {
    mt: 1,
    ...inputFieldStyle,
  },
  theoryExamLanguageField: {
    ...inputFieldStyle,
  },
  radioGroupField: {
    "& .MuiFormControlLabel-label ": {
      fontSize: "14px",
    },
    "& .MuiRadio-root": {
      padding: "6px",
    },
  },
  birthDateField: {
    ...inputFieldStyle,
  },
  genderIcon: {
    fontSize: "1.2rem",
    ml: "4px",
    position: "relative",
    top: "-1px",
  },
  selectInputField: {
    width: "100%",
    ...inputFieldStyle,
  },
};

export function ProfileCard(props: { trainingIncludesTheoryLessonsOrExam: boolean }) {
  const { trainingIncludesTheoryLessonsOrExam } = props;
  const student = useRecordContext<Student>();
  const canManageExternalProgress = grants.has("ExternalTrainingProgress:manage");
  const [editingExternalProgress, setEditingExternalProgress] = useState<ExternalTrainingProgress>();
  const {
    dialogProps: externalProgressDialogProps,
    isDialogOpen,
    openDialog: openExternalProgressDialog,
  } = useDialog();
  const {
    data: externalProgress,
    isLoading: isLoadingExternalProgress,
    isError: isExternalProgressError,
    refetch: refetchExternalProgress,
  } = useQuery(
    ["externalTrainingProgress", student.id],
    async () => (await getAuthenticatedServerClient()).listExternalTrainingProgress({ params: { id: student.id } }),
    {
      enabled: canManageExternalProgress,
      refetchOnMount: "always",
      onError: (error) => handleError("Der externe Ausbildungsstand konnte nicht geladen werden.", error),
    },
  );
  const availableClasses = DRIVING_LICENSE_CLASSES_CHOICES.filter(
    (choice) => !externalProgress?.some((progress) => progress.drivingLicenseClass === choice.id),
  );
  const openCreateExternalProgress = () => {
    setEditingExternalProgress(undefined);
    openExternalProgressDialog();
  };
  const openEditExternalProgress = (progress: ExternalTrainingProgress) => {
    setEditingExternalProgress(progress);
    openExternalProgressDialog();
  };
  const stripePaymentsEnabled = useStripePaymentsEnabled(student.drivingSchoolId);
  const paymentStrategyLabel =
    PaymentStrategyChoices.find((it) => it.id === student.paymentStrategy)?.name ?? "Keine Angabe";
  const hasSimulatorObligatedClass = student.bookedTrainings.some(isForCarOnly);

  return (
    <Box
      className="StudentDetails-profileColumn"
      sx={{
        height: "100%",
        flex: "0 0 300px",
        width: "300px",
        maxWidth: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        "@container student-details-layout (min-width: 1400px)": {
          flex: "0 0 340px",
          width: "340px",
        },
        "@container student-details-layout (max-width: 751px)": {
          flex: "1 1 100%",
          width: "100%",
        },
      }}
    >
      <Card className="StudentDetails-nameCard" sx={{ ...overviewCardStyle, p: 0 }}>
        <Column sx={{ padding: "20px 30px 20px 30px" }}>
          <Row>
            <BackButton />
          </Row>
          <Row spacing="15px" alignItems="center" mt={2.5} mb={1.5}>
            <_Avatar />
            <Column sx={{ ml: "20px", minWidth: 0, flex: 1 }} justifyContent="center" spacing={-0.5}>
              <StudentLabels student={student} />
              <Column justifyContent="center" spacing={0.5}>
                <_EditableNameHeader />
                <_StatusField />
              </Column>
            </Column>
          </Row>
          <Row sx={{ gap: 3, alignItems: "baseline" }} justifyContent="center" mb={-0.5}>
            <StudentButtons
              onCreateExternalProgress={openCreateExternalProgress}
              canCreateExternalProgress={
                canManageExternalProgress &&
                !isLoadingExternalProgress &&
                !isExternalProgressError &&
                availableClasses.length > 0
              }
            />
          </Row>
        </Column>
      </Card>
      <Card className="StudentDetails-profileDetailsCard" sx={{ ...overviewCardStyle, p: 0 }}>
        <_ContactDetails
          externalProgress={canManageExternalProgress ? externalProgress : undefined}
          isLoadingExternalProgress={canManageExternalProgress && isLoadingExternalProgress}
          isExternalProgressError={canManageExternalProgress && isExternalProgressError}
          onRetryExternalProgress={() => void refetchExternalProgress()}
          onEditExternalProgress={openEditExternalProgress}
        />
        <Divider />
        <FlatAccordion
          title={
            student.dateOfBirth ? (
              <>
                <span>{`Geboren: ${formatDate(student.dateOfBirth)}`}</span>
                {student.gender === "Male" && (
                  <Tooltip title="Männlich">
                    <MaleIcon sx={profileCardStyles.genderIcon} />
                  </Tooltip>
                )}
                {student.gender === "Female" && (
                  <Tooltip title="Weiblich">
                    <FemaleIcon sx={profileCardStyles.genderIcon} />
                  </Tooltip>
                )}
              </>
            ) : (
              "Geboren: Keine Angabe"
            )
          }
          subtitle={_getBirthSubtitle(student)}
        >
          <Stack spacing={2}>
            <LabeledAndEditable
              label="Geburtsname"
              disableEdit={!grants.has("editStudentPersonalData")}
              renderInput={() => <TextInput source="birthName" />}
            >
              <TextField source="birthName" emptyText="Keine Angabe" />
            </LabeledAndEditable>
            <LabeledAndEditable
              label="Geburtsdatum"
              source="dateOfBirth"
              disableEdit={!grants.has("editStudentPersonalData")}
              renderInput={() => (
                <DateInput source="dateOfBirth" allowPastDates sx={profileCardStyles.birthDateField} />
              )}
            >
              <Row gap={0.5} flexWrap="wrap">
                <DateField source="dateOfBirth" />
                <AgeField source="dateOfBirth" />
              </Row>
            </LabeledAndEditable>
            <LabeledAndEditable
              label="BF17"
              renderInput={({ submit }) => (
                <Row>
                  <RadioGroupInput
                    source="bf17"
                    label="BF17"
                    options={BF17_CHOICES}
                    sx={{
                      label: { position: "relative", transform: "unset" },
                      ...profileCardStyles.radioGroupField,
                    }}
                    onChange={(value) => submit({ bf17: value.bf17 === "true" })}
                  />
                </Row>
              )}
              disableEdit={!grants.has("editStudentPersonalData")}
            >
              <FunctionField render={_renderBf17} source="bf17" />
            </LabeledAndEditable>
            <LabeledAndEditable
              label="Geburtsort"
              disableEdit={!grants.has("editStudentPersonalData")}
              renderInput={() => <TextInput source="cityOfBirth" />}
            >
              <TextField source="cityOfBirth" />
            </LabeledAndEditable>
            <LabeledAndEditable
              label="Geschlecht"
              renderInput={({ submit }) => (
                <Row>
                  <RadioGroupInput
                    source="gender"
                    label="Geschlecht"
                    options={GENDER_CHOICES.map((it) => [it.id, it.name])}
                    sx={{
                      label: { position: "relative", transform: "unset" },
                      ...profileCardStyles.radioGroupField,
                    }}
                    onChange={(value) => submit(value)}
                  />
                </Row>
              )}
              disableEdit={!grants.has("editStudentPersonalData")}
            >
              <FunctionField render={_renderGender} source="gender" />
            </LabeledAndEditable>
            <LabeledAndEditable
              label="Anrede"
              renderInput={({ submit }) => (
                <AutoSubmitSelectInput
                  source="salutation"
                  label="Anrede"
                  submit={submit}
                  sx={profileCardStyles.selectInputField}
                  InputComponent={SalutationInput}
                />
              )}
              disableEdit={!grants.has("editStudentPersonalData")}
            >
              <TextField source="salutation" emptyText="Keine Angabe" />
            </LabeledAndEditable>
            <StudentGuardianDetails student={student} />
          </Stack>
        </FlatAccordion>
        <Divider />
        <FlatAccordion title="Sprache & Nationalität" subtitle={_getLanguageAndNationalitySubtitle(student)}>
          <Stack spacing={2}>
            <LabeledAndEditable
              label="Sprache für Theorieprüfung"
              disableEdit={!grants.has("editStudentPersonalData")}
              renderInput={({ submit }) => (
                <AutoSubmitSelectInput
                  source="theoryExamLanguage"
                  label="Sprache für Theorieprüfung"
                  submit={submit}
                  sx={profileCardStyles.selectInputField}
                  InputComponent={TheoryExamLanguageInput}
                />
              )}
            >
              <TheoryExamLanguageField source="theoryExamLanguage" />
            </LabeledAndEditable>
            <LabeledAndEditable
              label="Staatsangehörigkeit"
              disableEdit={!grants.has("editStudentPersonalData")}
              renderInput={({ submit }) => (
                <AutoSubmitSelectInput
                  source="nationality"
                  label="Staatsangehörigkeit"
                  submit={submit}
                  sx={profileCardStyles.selectInputField}
                  InputComponent={NationalityInput}
                />
              )}
            >
              <NationalityField source="nationality" />
            </LabeledAndEditable>
          </Stack>
        </FlatAccordion>
        <Divider />
        {stripePaymentsEnabled && (
          <FlatAccordion title="Zahlungsdaten" subtitle={paymentStrategyLabel}>
            <Column spacing={2}>
              <_PaymentDetailsField />
              {(grants.has("viewDunning") || grants.has("FundingInstructions:view")) && (
                <Labeled label="Banküberweisungsdaten">
                  <FundingInstructionsField />
                </Labeled>
              )}
            </Column>
          </FlatAccordion>
        )}
        <Divider />
        <FlatAccordion title="Login">
          <Column spacing={2}>
            <Labeled
              label={
                grants.has("resetPassword") ? (
                  <>
                    Login (<ResetPasswordLink label="Passwort setzen" />)
                  </>
                ) : (
                  "Login"
                )
              }
            >
              <AuthProvidersField sx={profileCardStyles.authProviderField} />
            </Labeled>
            <Labeled label="Letzter Login">
              <Box>
                <DateField source="lastLogin.at" showTime />
                &nbsp;
                {student.lastLogin && (
                  <>
                    (<TextField source="lastLogin.platform" sx={{ ml: 0 }} />)
                  </>
                )}
              </Box>
            </Labeled>
          </Column>
        </FlatAccordion>
        <Divider />
        <FlatAccordion title="Rechnungsadresse">
          <Column spacing={2}>
            <_InvoiceAddressField />
            <LabeledAndEditable
              label="E-Mail-Adresse für Rechnungen"
              renderInput={() => <TextInput source="invoiceAddress.email" validate={email()} />}
              disableEdit={!grants.has("editStudentPersonalData")}
            >
              <TextField source="invoiceAddress.email" />
            </LabeledAndEditable>
          </Column>
        </FlatAccordion>
        <Divider />
        <FlatAccordion title="Berechtigungen">
          <Column spacing={2}>
            {trainingIncludesTheoryLessonsOrExam && <_StudentAvailableTheoryBranchesField />}
            <LabeledAndEditable
              label="Fahrschüler darf…"
              renderInput={({ cancel }) => (
                <LabeledBox label="Berechtigungen" sx={{ ml: "-1em", pl: "calc(1em - 1.6px)" }} onClose={cancel}>
                  <StudentFeatureToggles mode="edit" />
                </LabeledBox>
              )}
            >
              <StudentFeatureToggles mode="show" />
            </LabeledAndEditable>
            {hasSimulatorObligatedClass && (
              <LabeledAndEditable
                label="Pflicht Simulatorstunden (UE)"
                disableEdit={!grants.has("editStudentPersonalData")}
                renderInput={() => <NumberInput source="minimumSimulatorUE" min={0} max={100} step={1} />}
              >
                <NumberField source="minimumSimulatorUE" emptyText="Kein Minimum konfiguriert" />
              </LabeledAndEditable>
            )}
            <LabeledAndEditable
              label="Fahrstunden Wochenlimit (UE)"
              disableEdit={!grants.has("editStudentPersonalData")}
              renderInput={() => <NumberInput source="maxBookableDrivingLessonsPerWeekLU" min={0} max={20} step={1} />}
            >
              <NumberField source="maxBookableDrivingLessonsPerWeekLU" emptyText="Kein Limit konfiguriert" />
            </LabeledAndEditable>
            {grants.has("editStudentBudget") && student.paymentStrategy === "purchaseOnAccount" && (
              <LabeledAndEditable
                label="Budget"
                disableEdit={!grants.has("editStudentBudget")}
                renderInput={() => <CentsInput label="Budget" source="budget" />}
              >
                <MoneyField source="budget" />
              </LabeledAndEditable>
            )}
          </Column>
        </FlatAccordion>
      </Card>
      {canManageExternalProgress && isDialogOpen && (
        <ExternalTrainingProgressDialog
          {...externalProgressDialogProps}
          studentId={student.id}
          progress={editingExternalProgress}
          availableClasses={availableClasses}
        />
      )}
    </Box>
  );
}

function _ContactDetails(props: {
  externalProgress?: ExternalTrainingProgress[];
  isLoadingExternalProgress: boolean;
  isExternalProgressError: boolean;
  onRetryExternalProgress: () => void;
  onEditExternalProgress: (progress: ExternalTrainingProgress) => void;
}) {
  const {
    externalProgress,
    isLoadingExternalProgress,
    isExternalProgressError,
    onRetryExternalProgress,
    onEditExternalProgress,
  } = props;
  return (
    <FlatAccordion
      title="Basisdaten"
      sx={{
        "@container student-details-layout (min-width: 752px)": {
          "& > .MuiAccordionSummary-root": { display: "none" },
          "& > .MuiCollapse-root": {
            height: "auto !important",
            overflow: "visible",
            visibility: "visible",
          },
          "& > .MuiCollapse-root > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner > [role='region'] > .MuiAccordionDetails-root":
            { padding: "25px 30px" },
        },
      }}
    >
      <Stack spacing={2}>
        <_PostalAddressField />
        <LabeledAndEditable
          label="E-Mail-Adresse für Benachrichtigungen"
          renderInput={() => <TextInput source="email" sx={{ ml: 0 }} />}
          disableEdit={!grants.has("editStudentPersonalData")}
        >
          <TextField source="email" />
        </LabeledAndEditable>
        <_PhoneNumberField />
        <_PreviousDrivingLicenseClassesField />
        {grants.has("ExternalTrainingProgress:manage") && (
          <ExternalTrainingProgressField
            progress={externalProgress}
            isLoading={isLoadingExternalProgress}
            isError={isExternalProgressError}
            onRetry={onRetryExternalProgress}
            onEdit={onEditExternalProgress}
          />
        )}
      </Stack>
    </FlatAccordion>
  );
}

function _EditableNameHeader() {
  const { dialogProps, openDialog } = useDialog();
  const student = useRecordContext<Student>();
  const disableEdit = !grants.has("editStudentPersonalData");

  return (
    <>
      <Box
        onClick={disableEdit ? undefined : openDialog}
        onKeyDown={(event) => {
          if (disableEdit || event.repeat) {
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDialog();
          }
        }}
        tabIndex={disableEdit ? undefined : 0}
        role={disableEdit ? undefined : "button"}
        aria-label={disableEdit ? undefined : "Name bearbeiten"}
        sx={{
          cursor: disableEdit ? "default" : "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          width: "100%",
          minWidth: 0,
          textAlign: "left",
          "& h2": {
            minWidth: 0,
            overflowWrap: "anywhere",
          },
          "@container student-details-layout (min-width: 752px) and (max-width: 1399px)": {
            "& h2": { fontSize: "20px" },
          },
          "&:hover .edit-icon": {
            opacity: 1,
          },
        }}
      >
        <NameHeader>{student.name}</NameHeader>
        {!disableEdit && (
          <EditIcon
            className="edit-icon"
            sx={{
              fontSize: "1.2rem",
              opacity: 0,
              transition: "opacity 0.2s",
              color: "primary.main",
              flexShrink: 0,
            }}
          />
        )}
      </Box>
      {dialogProps.open && <EditStudentNameDialog {...dialogProps} />}
    </>
  );
}

function _Avatar() {
  const student = useRecordContext<Student>();
  return (
    <Badge
      badgeContent={
        student.archived ? (
          <Tooltip title="Schüler archiviert" arrow>
            <ArchiveOutlinedIcon
              className="StudentDetails-avatarBadgeIcon"
              sx={{
                ...dotStyle2,
                backgroundColor: autovioColors.grey,
                padding: "2px",
                color: autovioColors.white,
              }}
            />
          </Tooltip>
        ) : null
      }
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{
        "& .StudentDetails-avatar": {
          width: "60px",
          height: "60px",
        },
        "& .MuiBadge-badge": {
          top: "20%",
          right: "12%",
          pointerEvents: "auto",
        },
        "@container student-details-layout (min-width: 752px) and (max-width: 1399px)": {
          "& .StudentDetails-avatar": {
            width: "50px",
            height: "50px",
          },
          "& .StudentDetails-avatarBadgeIcon": {
            width: "16px",
            height: "16px",
          },
        },
      }}
    >
      <Badge
        badgeContent={
          student.needsGlasses ? (
            <Tooltip title="Sehhilfe benötigt" arrow>
              <span>
                <EyeGlassesIcon
                  className="StudentDetails-avatarBadgeIcon"
                  sx={{
                    ...dotStyle2,
                    padding: "2px",
                    color: autovioColors.white,
                  }}
                  viewBox="0 0 16 16"
                />
              </span>
            </Tooltip>
          ) : null
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{
          "& .MuiBadge-badge": {
            top: "20%",
            left: "12%",
            pointerEvents: "auto",
          },
        }}
      >
        <Avatar className="StudentDetails-avatar" src={student.avatarUrl} />
      </Badge>
    </Badge>
  );
}

function StudentButtons(props: { onCreateExternalProgress: () => void; canCreateExternalProgress: boolean }) {
  const { onCreateExternalProgress, canCreateExternalProgress } = props;
  const student = useRecordContext<Student>();

  const handlePhoneClick = () => {
    window.location.href = `tel:${student.phoneNumber}`;
  };

  const forwardToHubspot = (interaction: "email" | "whats-app") => {
    const url = `https://app.hubspot.com/contacts/19951845/record/0-1/${student.hubspotContactId}/?interaction=${interaction}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleEmailClick = () => {
    if (grants.has("viewHubspot")) {
      return forwardToHubspot("email");
    }
    window.open(`mailto:${student.email}`, "_blank", "noopener,noreferrer");
  };

  const handleWhatsappClick = () => {
    if (grants.has("viewHubspot")) {
      return forwardToHubspot("whats-app");
    }
    if (!student.phoneNumber) {
      return;
    }
    const url = _whatsappLink({ phoneNumber: student.phoneNumber });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Row sx={{ display: "block" }}>
      <Box sx={{ display: "flex" }}>
        {grants.has("Student:assignLabel") && <LabelsMenu student={student} />}
        <CreateDocumentMenu student={student} />
        <Tooltip title="E-Mail">
          <IconButton onClick={handleEmailClick}>
            <EmailIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="WhatsApp">
          <IconButton onClick={handleWhatsappClick}>
            <WhatsappIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Anrufen">
          <IconButton onClick={handlePhoneClick}>
            <LocalPhoneIcon />
          </IconButton>
        </Tooltip>
        <MoreMenu
          onCreateExternalProgress={onCreateExternalProgress}
          canCreateExternalProgress={canCreateExternalProgress}
        />
      </Box>
    </Row>
  );
}

function LabelsMenu({ student }: { student: Student }) {
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  return (
    <>
      <Tooltip title="Labels">
        <IconButton onClick={openMenu} data-testid="studentLabelsMenuButton">
          <LabelOutlinedIcon />
        </IconButton>
      </Tooltip>
      <StudentLabelsMenu student={student} anchorEl={anchorEl} open={open} onClose={closeMenu} />
    </>
  );
}

function MoreMenu(props: { onCreateExternalProgress: () => void; canCreateExternalProgress: boolean }) {
  const { onCreateExternalProgress, canCreateExternalProgress } = props;
  const { anchorEl, open, openMenu, closeMenu } = useMenu();
  const {
    dialogProps: hubspotIdDialogProps,
    openDialog: openHubspotIdDialog,
    closeDialog: closeHubspotIdDialog,
  } = useDialog();
  const student = useRecordContext<Student>();
  const showPreTheoryExamWorkflowDialog = student.needsTheoryExam && !student.isReadyForTheoryExam;
  const showArchivedMenuItem = grants.has("editStudentPersonalData");
  if (
    !showPreTheoryExamWorkflowDialog &&
    !showArchivedMenuItem &&
    !(
      grants.has("viewStripe") ||
      grants.has("viewHubspot") ||
      grants.has("viewFirestore") ||
      grants.has("ExternalTrainingProgress:manage")
    )
  ) {
    return null;
  }
  return (
    <>
      <Tooltip title="Weitere Aktionen">
        <IconButton onClick={openMenu} data-testid="studentMoreMenuButton">
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={closeMenu}>
        {grants.has("viewStripe") && <OpenInStripeMenuItem closeMenu={closeMenu} />}
        {showPreTheoryExamWorkflowDialog && <OpenPreTheoryExamWorkflowDialogMenuItem closeMenu={closeMenu} />}
        {grants.has("viewHubspot") && student.hubspotContactId && <OpenInHubspotMenuItem closeMenu={closeMenu} />}
        {grants.has("editHubspotContactId") && (
          <HubspotIdMenuItem closeMenu={closeMenu} openDialog={openHubspotIdDialog} />
        )}
        {grants.has("developer") && <OpenInFirestoreMenuItem closeMenu={closeMenu} />}
        {grants.has("developer") && <CopyIdIntoClipboardMenuItem closeMenu={closeMenu} />}
        {grants.has("developer") && <CopyVogelIdIntoClipboardMenuItem closeMenu={closeMenu} />}
        {grants.has("ExternalTrainingProgress:manage") && (
          <MenuItem
            disabled={!canCreateExternalProgress}
            onClick={() => {
              closeMenu();
              onCreateExternalProgress();
            }}
          >
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText>Externer Ausbildungsstand</ListItemText>
          </MenuItem>
        )}
        {grants.has("editStudentPersonalData") && <EyeGlassesMenuItem closeMenu={closeMenu} />}
        {showArchivedMenuItem && <ArchivedStudentMenuItem closeMenu={closeMenu} />}
      </Menu>
      <PreTheoryExamWorkflowDialog />
      <HubspotIdDialog dialogProps={hubspotIdDialogProps} closeDialog={closeHubspotIdDialog} />
    </>
  );
}

function StudentGuardianDetails({ student }: { student: Student }) {
  const { dialogProps, openDialog } = useDialog("EditStudentGuardianDialog");
  const disableEdit = !grants.has("editStudentPersonalData");
  if (!student.guardian) return null;
  const { firstName, lastName, email, phone } = student.guardian;
  return (
    <>
      <LabeledAndEditable
        label="Gesetzlicher Vertreter"
        onClick={disableEdit ? undefined : openDialog}
        disableEdit={disableEdit}
      >
        <Row spacing={1} alignItems="center">
          <Column>
            <Typography component="span" variant="body2">
              {`${firstName} ${lastName}`}
            </Typography>
            <Typography component="span" variant="body2">
              {email}
            </Typography>
            <Typography component="span" variant="body2">
              {phone}
            </Typography>
          </Column>
        </Row>
      </LabeledAndEditable>
      {!disableEdit && <EditStudentGuardianDialog {...dialogProps} />}
    </>
  );
}

function _StatusField() {
  const { dialogProps, openDialog } = useDialog();
  const disableEdit = !grants.has("changeStatusOfStudent");
  return (
    <>
      <Box
        onClick={disableEdit ? undefined : openDialog}
        onKeyDown={(event) => {
          if (disableEdit || event.repeat) {
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDialog();
          }
        }}
        tabIndex={disableEdit ? undefined : 0}
        role={disableEdit ? undefined : "button"}
        aria-label={disableEdit ? undefined : "Status bearbeiten"}
        sx={{
          cursor: disableEdit ? "default" : "pointer",
          userSelect: "none",
          display: "inline-flex",
          alignSelf: "flex-start",
          alignItems: "center",
          gap: 0.5,
          textAlign: "left",
          "&:hover .edit-icon": {
            opacity: 1,
          },
        }}
      >
        <TextField label="Status" source="verboseStatus" sx={{ color: "text.secondary" }} />
        {!disableEdit && (
          <EditIcon
            className="edit-icon"
            sx={{
              fontSize: "1rem",
              opacity: 0,
              transition: "opacity 0.2s",
              color: "primary.main",
            }}
          />
        )}
      </Box>
      {!disableEdit && <ChangeStudentStatusDialog {...dialogProps} />}
    </>
  );
}

function _PaymentDetailsField() {
  const { dialogProps, openDialog } = useDialog("ChangeStudentPaymentStrategyDialog");
  const disableEdit = !grants.has("editStudentPaymentStrategy");
  return (
    <>
      <LabeledAndEditable
        label="Zahlungsmodus"
        onClick={disableEdit ? undefined : openDialog}
        disableEdit={disableEdit}
      >
        <Row spacing={2} alignItems="center">
          <Column>
            <SelectField source="paymentStrategy" choices={PaymentStrategyChoices} />
            <_PaymentMethodLabel />
          </Column>
        </Row>
      </LabeledAndEditable>
      {!disableEdit && <ChangeStudentPaymentStrategyDialog {...dialogProps} />}
    </>
  );
}

function _PaymentMethodLabel() {
  const student = useRecordContext<Student>();

  function _renderPaymentMethod(student: Student) {
    const { paymentMethod } = student;
    switch (paymentMethod) {
      case "card":
        return "Kreditkarte";
      case "sepa":
        return "Lastschrift";
      default:
        return "Keine Angabe";
    }
  }

  if (student.paymentStrategy === "purchaseOnAccount") {
    return null;
  }

  return <FunctionField render={_renderPaymentMethod} />;
}

function _renderGender(record: Student) {
  return GENDER_CHOICES.find((it) => it.id === record.gender)?.name ?? "unbekannt";
}

function _renderBf17(record: Student) {
  return record.bf17 ? "Ja" : "Nein";
}

function _renderPreviousDrivingLicenseClasses(record: Student) {
  const classes = record.previousDrivingLicenseClasses ?? [];
  if (!classes.length) return "–";
  return (
    <Stack direction="row" gap={0.5} flexWrap="wrap">
      {classes.map((drivingLicenseClass) => (
        <Chip key={drivingLicenseClass} label={drivingLicenseClass} size="small" />
      ))}
    </Stack>
  );
}

function _PreviousDrivingLicenseClassesField() {
  const { dialogProps, openDialog } = useDialog();
  const disableEdit = !grants.has("editStudentPersonalData");

  return (
    <>
      <LabeledAndEditable
        label="Vorbesitz Führerscheinklassen"
        onClick={disableEdit ? undefined : openDialog}
        disableEdit={disableEdit}
      >
        <FunctionField render={_renderPreviousDrivingLicenseClasses} source="previousDrivingLicenseClasses" />
      </LabeledAndEditable>
      {!disableEdit && <EditDrivingLicenseClassesDialog {...dialogProps} />}
    </>
  );
}

function _PostalAddressField() {
  const { dialogProps, openDialog } = useDialog();
  const disableEdit = !grants.has("editStudentPersonalData");
  const student = useRecordContext<Student>();
  const postalAddress = student?.postalAddress;
  const handleLocationClick = () => {
    const googleMapsUrl = getGoogleMapsUrl(postalAddress);
    if (googleMapsUrl) {
      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
    }
  };

  const googleMapsUrl = getGoogleMapsUrl(postalAddress);

  return (
    <>
      <LabeledAndEditable
        label="Wohnort"
        onClick={disableEdit ? undefined : openDialog}
        disableEdit={disableEdit}
        endIcon={<RoomOutlinedIcon fontSize="small" />}
        endIconClick={googleMapsUrl ? handleLocationClick : undefined}
        endIconAriaLabel="Adresse in Google Maps öffnen"
      >
        {postalAddress ? (
          <AddressDisplay address={postalAddress} />
        ) : (
          <Typography variant="body2">Keine Angabe</Typography>
        )}
      </LabeledAndEditable>
      {!disableEdit && <EditPostalAddressDialog {...dialogProps} />}
    </>
  );
}

function _InvoiceAddressField() {
  const { dialogProps, openDialog } = useDialog();
  const disableEdit = !grants.has("editStudentPersonalData");
  return (
    <>
      <LabeledAndEditable
        label="Rechnungsadresse"
        onClick={disableEdit ? undefined : openDialog}
        disableEdit={disableEdit}
      >
        <InvoiceAddressField source="invoiceAddress" emptyText="Keine Angabe" />
      </LabeledAndEditable>
      {!disableEdit && <EditInvoiceAddressDialog {...dialogProps} />}
    </>
  );
}

function _PhoneNumberField() {
  const student = useRecordContext<Student>();

  const handlePhoneClick = () => {
    window.location.href = `tel:${student.phoneNumber}`;
  };

  return (
    <LabeledAndEditable
      label="Telefonnummer"
      disableEdit={!grants.has("editStudentPersonalData")}
      renderInput={() => <TextInput source="phoneNumber" />}
      endIcon={<LocalPhoneIcon fontSize="small" />}
      endIconClick={student.phoneNumber ? handlePhoneClick : undefined}
      endIconAriaLabel="Telefonnummer anrufen"
    >
      <TextField source="phoneNumber" />
    </LabeledAndEditable>
  );
}

function _whatsappLink({ phoneNumber }: { phoneNumber: string }): string {
  let normalizedPhoneNumber = phoneNumber.replaceAll(/[^\d]/g, "");
  if (normalizedPhoneNumber.match(/^0[1-9]/)) {
    normalizedPhoneNumber = `49${normalizedPhoneNumber.substring(1)}`;
  } else {
    normalizedPhoneNumber = normalizedPhoneNumber.replace(/^0*/, "");
  }
  return `https://wa.me/${normalizedPhoneNumber}`;
}

function _getBirthSubtitle(student: Student | undefined): string | undefined {
  if (!student || !student.dateOfBirth) {
    return undefined;
  }
  const { dateOfBirth } = student;
  const age = dateOfBirth.toDateTime().startOf("day").diffNow(["years", "months"]).negate().shiftToAll();
  const years = Duration.fromObject({ years: age.years }, { locale: "de" });
  const months = Duration.fromObject({ months: age.months }, { locale: "de" });
  const parts = [years.toHuman()];
  if (months.months !== 0) {
    parts.push(months.toHuman());
  }
  const ageString = parts.join(", ");

  return `${ageString}${student.cityOfBirth ? `, ${student.cityOfBirth}` : ""}`;
}

function _getLanguageAndNationalitySubtitle(student: Student | undefined): string | undefined {
  if (!student) {
    return undefined;
  }
  const parts: string[] = [];
  if (student.theoryExamLanguage) {
    const languageName = AUTOVIO_SUPPORTED_LANGUAGES.find((lang) => lang[0] === student.theoryExamLanguage)?.[1];
    if (languageName) parts.push(`${languageName} (Theorie)`);
  }
  if (student.nationality) {
    const nationalityName = nationalities[student.nationality as keyof typeof nationalities];
    if (nationalityName) parts.push(nationalityName);
  }
  return parts.length > 0 ? parts.join(", ") : undefined;
}

function _StudentAvailableTheoryBranchesField() {
  const { dialogProps, openDialog } = useDialog("StudentTheoryBranchesDialog");
  const student = useRecordContext<Student>();
  const { data: drivingSchool } = useGetOne<DrivingSchool>("drivingSchools", { id: student.drivingSchoolId });

  if (Object.values(drivingSchool?.branches ?? {}).length <= 1) {
    return null;
  }

  return (
    <>
      <LabeledAndEditable label="Kann Unterricht buchen in..." onClick={openDialog}>
        <FunctionField render={_renderTheoryBranches} source="theoryBranches" />
      </LabeledAndEditable>
      <StudentTheoryBranchesDialog drivingSchool={drivingSchool!} student={student} {...dialogProps} />
    </>
  );
}

function _renderTheoryBranches(student: Student) {
  const drivingSchool = drivingSchoolsProvider.getOneFromCache(student.drivingSchoolId);
  const numDrivingSchoolBranches = Object.values(drivingSchool?.branches ?? {}).length;
  const theoryBranches = student.theoryBranches;
  const numTheoryBranches = theoryBranches.length;

  if (numTheoryBranches === 0) {
    return <Typography sx={{ fontSize: 14, color: autovioColors.red }}>Keiner Filiale</Typography>;
  }
  return (
    <Typography sx={{ fontSize: 14 }}>
      {numTheoryBranches === numDrivingSchoolBranches
        ? "Alle Filialen"
        : theoryBranches
            .map((it) => drivingSchool?.branches.find((branch) => branch.id === it)?.name)
            .filter(Boolean)
            .join(", ")}
    </Typography>
  );
}
